import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, Building2, Home, ScrollText, Lock, Unlock,
  AlertTriangle, FileEdit, Eye, MapPin,
} from "lucide-react";

const LOCKABLE = [
  { v: "price", label: "Price" },
  { v: "main_area", label: "Main Area" },
  { v: "sub_area", label: "Sub Area" },
  { v: "title", label: "Title" },
  { v: "property_type", label: "Property Type" },
  { v: "bhk", label: "BHK" },
];

export default function AdminApprovalsTab() {
  const [queue, setQueue] = useState({ items: [], by_kind: {}, total: 0 });
  const [active, setActive] = useState(null); // selected item
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [activity, setActivity] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const r = await api.get("/admin/approvals");
      setQueue(r.data || { items: [], total: 0 });
    } catch (e) {
      toast.error("Failed to load approvals queue");
    }
  };
  useEffect(() => { load(); }, []);

  // v2.72 — Live queue: refreshes when any property/project mutation happens
  // anywhere (other tabs, other admin sessions via the WS-notification
  // piggyback) + a 45 s background poll while the tab is visible.
  useDataSync(["approvals", "properties", "projects"], load);
  useAutoRefresh(load, 45000);

  // v2.71.101 — Auto-open the item targeted by `?id=<id>` (deep-link from
  // AdminListingsTab / AdminOverview pending rails). Strip the param after
  // we open the item so subsequent visits start blank.
  useEffect(() => {
    const targetId = searchParams.get("id");
    if (!targetId || active) return;
    const it = (queue.items || []).find((x) => x.id === targetId);
    if (it) {
      openItem(it);
      const next = new URLSearchParams(searchParams);
      next.delete("id");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, queue.items, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const openItem = async (it) => {
    setActive(it);
    setRejectOpen(false);
    setRejectReason("");
    if (it._kind === "property") {
      try {
        const r = await api.get(`/properties/${it.id}/activity`);
        setActivity(r.data || []);
      } catch { setActivity([]); }
    } else {
      setActivity([]);
    }
  };

  const approve = async () => {
    if (!active) return;
    const target = active;
    setBusy(true);
    try {
      const url = target._kind === "property"
        ? `/admin/properties/${target.id}/approve-v2`
        : `/admin/projects/${target.id}/approve`;
      // v2.72 — Optimistic: drop the item from the queue instantly; the
      // emit below triggers a silent reconcile fetch via useDataSync. On
      // failure load() restores the row.
      setQueue((q) => ({
        ...q,
        items: (q.items || []).filter((x) => x.id !== target.id),
        total: Math.max(0, (q.total || 0) - 1),
        by_kind: { ...q.by_kind, [target._kind]: Math.max(0, (q.by_kind?.[target._kind] || 0) - 1) },
      }));
      setActive(null);
      await api.post(url);
      toast.success(`${target._kind === "property" ? "Listing" : "Project"} approved`);
      emitDataChange(["properties", "projects", "approvals", "dashboard"], {
        action: "patch", id: target.id, patch: { approval_status: "approved" },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
      load();
    }
    setBusy(false);
  };

  const reject = async () => {
    if (!active || !rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    const target = active;
    setBusy(true);
    try {
      const url = target._kind === "property"
        ? `/admin/properties/${target.id}/reject-v2`
        : `/admin/projects/${target.id}/reject`;
      const body = { reason: rejectReason.trim() };
      // v2.72 — Optimistic removal (same pattern as approve).
      setQueue((q) => ({
        ...q,
        items: (q.items || []).filter((x) => x.id !== target.id),
        total: Math.max(0, (q.total || 0) - 1),
        by_kind: { ...q.by_kind, [target._kind]: Math.max(0, (q.by_kind?.[target._kind] || 0) - 1) },
      }));
      setActive(null);
      setRejectOpen(false);
      setRejectReason("");
      await api.post(url, body);
      toast.success(`Rejected with reason`);
      emitDataChange(["properties", "projects", "approvals", "dashboard"], {
        action: "patch", id: target.id, patch: { approval_status: "rejected" },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
      load();
    }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-5" data-testid="admin-approvals-tab">
      {/* Left — queue list */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden h-[calc(100vh-220px)] flex flex-col" data-testid="approvals-queue">
        <div className="px-4 py-3 border-b border-[#E4E4E7] bg-[#FAFAFA]">
          <h3 className="font-heading font-semibold text-[14px] tracking-tight inline-flex items-center gap-2">
            <Clock size={14} /> Pending approvals ({queue.total})
          </h3>
          <div className="text-[11px] text-[#71717A] mt-0.5">
            {queue.by_kind?.property || 0} listings · {queue.by_kind?.project || 0} projects
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {queue.items.length === 0 && (
            <div className="text-center py-10 text-[#A1A1AA] text-[12px]" data-testid="approvals-empty">
              <CheckCircle2 size={28} className="mx-auto mb-2 text-[#D4D4D8]" />
              All caught up. No pending submissions.
            </div>
          )}
          {queue.items.map((it) => (
            <button
              key={`${it._kind}-${it.id}`}
              type="button"
              onClick={() => openItem(it)}
              className={`w-full text-left px-4 py-3 border-b border-[#F4F4F5] hover:bg-[#FAFAFA] flex items-start gap-2.5 transition-colors ${active?.id === it.id ? "bg-[#EEF4FF]" : ""}`}
              data-testid={`approval-item-${it.id}`}
            >
              <div className="mt-0.5">
                {it._kind === "property" ? <Home size={13} className="text-[#0055FF]" /> : <Building2 size={13} className="text-[#9333EA]"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{it._kind === "property" ? "Listing" : "Project"}</div>
                <div className="text-[13px] font-semibold text-[#09090B] truncate">{it.title || it.name}</div>
                <div className="text-[11px] text-[#A1A1AA] inline-flex items-center gap-1 mt-0.5">
                  <MapPin size={10} /> {it.main_area || it.area || "—"} {it.sub_area && `· ${it.sub_area}`}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5">
                  by <b>{it.dealer_name || it.builder_name || "Unknown"}</b>
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-wider font-semibold bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded-sm">
                pending
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Right — detail + actions */}
      {!active ? (
        <div className="bg-white border border-dashed border-[#E4E4E7] rounded-sm flex items-center justify-center min-h-[60vh] text-[#A1A1AA]" data-testid="approvals-no-selection">
          <div className="text-center">
            <ScrollText size={36} className="mx-auto mb-3 text-[#D4D4D8]" />
            Select a submission from the left to review.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-y-auto max-h-[calc(100vh-220px)]" data-testid="approval-detail">
          <DetailHeader active={active} />
          <div className="px-6 py-5 space-y-6">
            <DetailGrid active={active} />
            {active._kind === "property" && <LockEditor pid={active.id} initial={active.locked_fields || []} onChange={load} />}

            {/* Action bar */}
            <div className="flex items-center gap-2 border-t border-[#E4E4E7] pt-4">
              <Button className="btn-brand rounded-sm" onClick={approve} disabled={busy} data-testid="approve-btn">
                <CheckCircle2 size={13} className="mr-1.5" /> Approve
              </Button>
              <Button variant="outline" className="rounded-sm border-[#FCA5A5] text-[#DC2626] hover:bg-[#FEE2E2]" onClick={() => setRejectOpen((v) => !v)} disabled={busy} data-testid="reject-btn">
                <XCircle size={13} className="mr-1.5" /> Reject
              </Button>
              {/* v2.71.103 — Bug fix: "Open in full editor" must route projects
                  to the projects section, not the listings section. Previously
                  both kinds went to /admin/listings?edit=<id>, which then
                  called GET /properties/<id> and returned 404 "Property not
                  found" for project IDs.

                  Properties → /admin/listings?edit=<id>  (existing flow)
                  Projects   → /admin/projects?edit=<id>  (new deep-link picked
                               up by AdminProjectsTab to auto-open the inline
                               project editor — falls back to the read-only
                               /project/<id> page when the user just wants to
                               preview). */}
              <a
                href={
                  active._kind === "project"
                    ? `/admin/projects?edit=${active.id}`
                    : `/admin/listings?edit=${active.id}`
                }
                className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-[#0055FF] font-semibold hover:underline"
                data-testid="full-edit-link"
              >
                <FileEdit size={12} /> Open in full editor
              </a>
            </div>

            {rejectOpen && (
              <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm p-4" data-testid="reject-form">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#991B1B] mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle size={11} /> Rejection reason (visible to dealer)
                </div>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g., Please add interior photos and verify the price."
                  rows={3}
                  className="rounded-sm bg-white"
                  data-testid="reject-reason-input"
                />
                <div className="flex gap-2 mt-3">
                  <Button onClick={reject} disabled={busy || !rejectReason.trim()} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-sm" data-testid="reject-confirm-btn">
                    Confirm Reject
                  </Button>
                  <Button variant="outline" className="rounded-sm" onClick={() => setRejectOpen(false)} data-testid="reject-cancel-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Activity log */}
            {activity.length > 0 && (
              <div className="border-t border-[#E4E4E7] pt-4" data-testid="activity-log">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B] mb-2 inline-flex items-center gap-1.5">
                  <Eye size={11} /> Activity log ({activity.length})
                </div>
                <div className="space-y-1.5">
                  {activity.map((a) => (
                    <div key={a.id} className="text-[11px] bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-2.5" data-testid={`activity-${a.id}`}>
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="font-semibold text-[#09090B]">{a.actor_name || a.actor_role}</span>
                        <span className="text-[#A1A1AA]">{new Date(a.at).toLocaleString()}</span>
                      </div>
                      <div className="text-[#52525B]">
                        <span className={`text-[9px] uppercase tracking-wider font-semibold mr-1.5 px-1.5 py-0.5 rounded-sm ${kindCls(a.kind)}`}>{a.kind}</span>
                        {a.payload?.reason && <span className="italic">"{a.payload.reason}"</span>}
                        {a.payload?.locked_fields && <span>locked: {a.payload.locked_fields.join(", ")}</span>}
                        {a.payload?.changed_fields && <span>changed: {a.payload.changed_fields.slice(0, 4).join(", ")}{a.payload.changed_fields.length > 4 ? "…" : ""}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const kindCls = (k) => {
  switch (k) {
    case "approved": return "bg-[#D1FAE5] text-[#065F46]";
    case "rejected": return "bg-[#FEE2E2] text-[#991B1B]";
    case "submitted": return "bg-[#DBEAFE] text-[#1E40AF]";
    case "locks_updated": return "bg-[#E9D5FF] text-[#6B21A8]";
    default: return "bg-[#F4F4F5] text-[#52525B]";
  }
};

function DetailHeader({ active }) {
  return (
    <div className="px-6 py-4 border-b border-[#E4E4E7] bg-[#FAFAFA]">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">{active._kind === "property" ? "Listing review" : "Project review"}</div>
      <h2 className="font-heading font-bold text-2xl tracking-tight text-[#09090B]">{active.title || active.name}</h2>
      <div className="text-[11px] text-[#71717A] mt-1">Submitted by <b className="text-[#09090B]">{active.dealer_name || active.builder_name || "—"}</b> · {active.created_at && new Date(active.created_at).toLocaleString()}</div>
    </div>
  );
}

function DetailGrid({ active }) {
  const fields = active._kind === "property" ? [
    ["Title", active.title], ["Price", `₹${active.price?.toLocaleString?.("en-IN") || "—"}`],
    ["Main Area", active.main_area || active.area || "—"], ["Sub Area", active.sub_area || "—"],
    ["BHK", active.bhk], ["Property Type", active.property_type],
    ["Size", active.area_display || (active.size_sqft ? `${active.size_sqft} ${active.size_unit === "sqft" ? "Sq Ft" : active.size_unit === "sqm" ? "Sq M" : active.size_unit === "acre" ? "Acre" : "Sq Yd"}` : "—")], ["Metro distance", active.distance_metro_m + "m"],
    ["Floor", active.floor || "—"], ["Furnishing", active.furnishing || "—"],
  ] : [
    ["Name", active.name], ["Builder / Owner", active.builder_name],
    ["Location", active.location], ["Status", active.project_status || "—"],
    ["Possession", active.possession || "—"],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="detail-grid">
      {fields.map(([label, value]) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</div>
          <div className="text-[13px] text-[#09090B] font-semibold">{value || "—"}</div>
        </div>
      ))}
      {active.notes && (
        <div className="col-span-full bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 text-[12px] text-[#52525B]">
          <b className="text-[#09090B]">Dealer notes:</b> {active.notes}
        </div>
      )}
    </div>
  );
}

function LockEditor({ pid, initial, onChange }) {
  const [locked, setLocked] = useState(new Set(initial || []));
  const [busy, setBusy] = useState(false);

  const toggle = (f) => {
    const next = new Set(locked);
    if (next.has(f)) next.delete(f); else next.add(f);
    setLocked(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/properties/${pid}/locks`, { locked_fields: Array.from(locked) });
      toast.success("Field locks updated");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#E4E4E7] rounded-sm p-4 bg-[#FAFAFA]" data-testid="lock-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B] inline-flex items-center gap-1.5">
            <Lock size={11} /> Field Lock System
          </div>
          <div className="text-[11px] text-[#71717A]">Lock fields so the dealer cannot change them after admin sets the value.</div>
        </div>
        <Button size="sm" className="btn-brand rounded-sm" onClick={save} disabled={busy} data-testid="lock-save-btn">Save locks</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {LOCKABLE.map((f) => {
          const on = locked.has(f.v);
          return (
            <button
              key={f.v}
              type="button"
              onClick={() => toggle(f.v)}
              className={`text-[11px] font-medium px-3 py-1.5 border rounded-full inline-flex items-center gap-1.5 transition-all ${on ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#0055FF]"}`}
              data-testid={`lock-toggle-${f.v}`}
            >
              {on ? <Lock size={10} /> : <Unlock size={10} />} {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
